import { useState, useRef, useCallback, useEffect } from 'react';
import { useQuery } from '@tanstack/react-query';
import { AxiosError } from 'axios';
import { saveAs } from 'file-saver';

import {
  getReportMeasurements,
  getReportMeasurementsSummary,
  getReportMeasurementsExcell,
  getReportMeasurementsPdf,
} from 'services/reports';
import { getMeasurementUnits } from 'services/collections';
import { getPartners } from 'services/partners';
import useTranslation from 'customHooks/translate';
import {
  MEASUREMENT_UNITS,
  PARTNERS_DATA,
  REPORTS_EXPENSES,
  REPORTS_INCOMES,
  REPORTS_MEASUREMENT_TYPES_SUMMARY,
} from 'events/types';
import CustomDialog from 'pages/Dialogs/CustomDialog';

import { ReportsTotalViewIncome } from '../components/ReportsTotalViewIncome';
import { ReportsTotalViewExpense } from '../components/ReportsTotalViewExpense';
import { ReportsTab } from '../components/ReportsTab';
import { ReportsMultiSelect } from '../components/reportsMultiSelect';
import IncomesTable from './IncomesTable';
import ExpensesTable from './ExpensesTable';
import {
  delay,
  isEmpty,
  openNotification,
  waitForReportCompletion,
} from 'utils';
import { MEASUREMENT_REPORT_KEY } from 'consts';

export const Measurement = () => {
  const translation = useTranslation();
  const [fromDate, setFromDate] = useState('');
  const [toDate, setToDate] = useState('');
  const [taxes, setTaxes] = useState<any>(null);
  const [partner, setPartner] = useState<any>(null);
  const [shouldFetch, setShouldFetch] = useState(false);

  const [confirmedFromDate, setConfirmedFromDate] = useState('');
  const [confirmedToDate, setConfirmedToDate] = useState('');
  const [confirmedTaxes, setConfirmedTaxes] = useState<any>(null);
  const [confirmedPartner, setConfirmedPartner] = useState<any>(null);
  const [activeTab, setActiveTab] = useState<'incomes' | 'expenses'>('incomes');
  const [open, setOpen] = useState<boolean>(true);
  const [pageSize] = useState(10);

  // Infinite scroll related states
  const [incomesPageIndex, setIncomesPageIndex] = useState(0);
  const [expensesPageIndex, setExpensesPageIndex] = useState(0);
  const [incomesTableData, setIncomesTableData] = useState<any[]>([]);
  const [expensesTableData, setExpensesTableData] = useState<any[]>([]);
  const [hasMoreIncomes, setHasMoreIncomes] = useState(true);
  const [hasMoreExpenses, setHasMoreExpenses] = useState(true);
  const [isLoadingMoreIncomes, setIsLoadingMoreIncomes] = useState(false);
  const [isLoadingMoreExpenses, setIsLoadingMoreExpenses] = useState(false);

  // Refs for scroll containers and loading states
  const incomesTableContainerRef = useRef<HTMLDivElement>(null);
  const expensesTableContainerRef = useRef<HTMLDivElement>(null);
  const incomesLoadingRef = useRef<HTMLDivElement>(null);
  const expensesLoadingRef = useRef<HTMLDivElement>(null);
  const isFetchingIncomesRef = useRef(false);
  const isFetchingExpensesRef = useRef(false);

  const handleSelect = (values: number[]) => {
    setTaxes(values);
  };

  const handleSelectPartners = (values: number[]) => {
    setPartner(values);
  };

  // Reset data function
  const resetData = () => {
    setIncomesTableData([]);
    setExpensesTableData([]);
    setIncomesPageIndex(0);
    setExpensesPageIndex(0);
    setHasMoreIncomes(true);
    setHasMoreExpenses(true);
  };

  const { data: taxesData } = useQuery<any, AxiosError, any[]>({
    queryKey: [MEASUREMENT_UNITS],
    queryFn: () => getMeasurementUnits(),
    select: (response) =>
      response.data.data.map((d) => ({ ...d, label: d.name, value: d.id })),
  });

  const { data: partnersData } = useQuery<any, AxiosError, any[]>({
    queryKey: [PARTNERS_DATA],
    queryFn: () => getPartners(),
    select: (response) =>
      response.data.data.map((d) => ({ ...d, label: d.name, value: d.id })),
  });

  // Function to fetch incomes data
  const fetchIncomesData = useCallback(
    async (currentPage: number, isNewRequest: boolean = false) => {
      if (isFetchingIncomesRef.current) return null;

      isFetchingIncomesRef.current = true;
      setIsLoadingMoreIncomes(true);

      try {
        const response = await getReportMeasurements(
          confirmedFromDate,
          confirmedToDate,
          true,
          confirmedTaxes && confirmedTaxes.length > 0
            ? confirmedTaxes
            : undefined,
          confirmedPartner && confirmedPartner.length > 0
            ? confirmedPartner
            : undefined,
          pageSize,
          currentPage
        );

        const data = response.data;

        // Ensure we have data to work with
        if (data?.data?.length) {
          // Set data based on whether this is a new request or not
          if (isNewRequest) {
            setIncomesTableData(data.data);

            // Show notification on initial load
            if (data.data.length === 0 || data.data === null) {
              openNotification(`${translation.reports.NoDataIncome}`);
            } else {
              openNotification(
                `${translation.reports.IncomesDataFound}`,
                'success'
              );
            }
          } else {
            setIncomesTableData((prev) => [...prev, ...data.data]);
          }

          // Check if we have more data to load
          const newTotalItems =
            (isNewRequest ? 0 : incomesTableData.length) + data.data.length;
          setHasMoreIncomes(newTotalItems < data.totalRecord);

          return data;
        }

        if (isNewRequest) {
          openNotification(`${translation.reports.NoDataIncome}`);
        }

        return null;
      } catch {
        return null;
      } finally {
        setIsLoadingMoreIncomes(false);
        isFetchingIncomesRef.current = false;
      }
    },
    [
      confirmedFromDate,
      confirmedToDate,
      confirmedTaxes,
      confirmedPartner,
      pageSize,
      incomesTableData.length,
      translation.reports.NoDataIncome,
      translation.reports.IncomesDataFound,
    ]
  );

  // Function to fetch expenses data
  const fetchExpensesData = useCallback(
    async (currentPage: number, isNewRequest: boolean = false) => {
      if (isFetchingExpensesRef.current) return null;

      isFetchingExpensesRef.current = true;
      setIsLoadingMoreExpenses(true);

      try {
        const response = await getReportMeasurements(
          confirmedFromDate,
          confirmedToDate,
          false,
          confirmedTaxes && confirmedTaxes.length > 0
            ? confirmedTaxes
            : undefined,
          confirmedPartner && confirmedPartner.length > 0
            ? confirmedPartner
            : undefined,
          pageSize,
          currentPage
        );

        const data = response.data;

        // Ensure we have data to work with
        if (data?.data?.length) {
          // Set data based on whether this is a new request or not
          if (isNewRequest) {
            setExpensesTableData(data.data);

            // Show notification on initial load
            if (data.data.length === 0 || data.data === null) {
              openNotification(`${translation.reports.NoDataExpense}`);
            } else {
              openNotification(
                `${translation.reports.ExpencesDataFound}`,
                'success'
              );
            }
          } else {
            setExpensesTableData((prev) => [...prev, ...data.data]);
          }

          // Check if we have more data to load
          const newTotalItems =
            (isNewRequest ? 0 : expensesTableData.length) + data.data.length;
          setHasMoreExpenses(newTotalItems < data.totalRecord);

          return data;
        }

        if (isNewRequest) {
          openNotification(`${translation.reports.NoDataExpense}`);
        }

        return null;
      } catch {
        return null;
      } finally {
        setIsLoadingMoreExpenses(false);
        isFetchingExpensesRef.current = false;
      }
    },
    [
      confirmedFromDate,
      confirmedToDate,
      confirmedTaxes,
      confirmedPartner,
      pageSize,
      expensesTableData.length,
      translation.reports.NoDataExpense,
      translation.reports.ExpencesDataFound,
    ]
  );

  useQuery({
    queryKey: [
      REPORTS_INCOMES,
      confirmedFromDate,
      confirmedToDate,
      confirmedTaxes,
      confirmedPartner,
    ],
    queryFn: () => fetchIncomesData(0, true),
    enabled: shouldFetch,
    onSettled: () => {
      setShouldFetch(false);
    },
  });

  useQuery({
    queryKey: [
      REPORTS_EXPENSES,
      confirmedFromDate,
      confirmedToDate,
      confirmedTaxes,
      confirmedPartner,
    ],
    queryFn: () => fetchExpensesData(0, true),
    enabled: shouldFetch,
    onSettled: () => {
      setShouldFetch(false);
    },
  });

  const { data: summaryData } = useQuery({
    queryKey: [
      REPORTS_MEASUREMENT_TYPES_SUMMARY,
      confirmedFromDate,
      confirmedToDate,
      confirmedTaxes,
      confirmedPartner,
    ],
    queryFn: () =>
      getReportMeasurementsSummary(
        confirmedFromDate,
        confirmedToDate,
        confirmedTaxes && confirmedTaxes.length > 0
          ? confirmedTaxes
          : undefined,
        confirmedPartner && confirmedPartner.length > 0
          ? confirmedPartner
          : undefined
      ),
    select: (res) => res.data,
    enabled: shouldFetch,
  });

  const setDateRange = (from: string, to: string) => {
    setFromDate(from);
    setToDate(to);
  };

  const handleConfirm = () => {
    if (!fromDate || !toDate || taxes === null || partner === null) {
      openNotification(`${translation.reports.Parameters}`, 'error');
      return;
    }
    setConfirmedFromDate(fromDate);
    setConfirmedToDate(toDate);
    setConfirmedTaxes(taxes);
    setConfirmedPartner(partner);
    resetData();
    setShouldFetch(true);
    setOpen(false);
  };

  const handleExcelExport = async () => {
    try {
      openNotification(
        `${translation.reports.ExcellWait}`,
        'info',
        0,
        MEASUREMENT_REPORT_KEY,
        true
      );

      const excelResponse = await getReportMeasurementsExcell(
        confirmedFromDate,
        confirmedToDate,
        confirmedTaxes,
        confirmedPartner
      );

      const reportId = excelResponse?.data?.[0]?.id;

      if (reportId) {
        await delay(4000);

        const downloadResponse = await waitForReportCompletion(
          String(reportId),
          translation
        );
        const blob = new Blob([downloadResponse.data], {
          type: 'application/vnd.ms-excel',
        });
        saveAs(blob, `report_${reportId}.xlsx`);

        openNotification(
          `${translation.reports.ExcellGenerated}`,
          'success',
          2,
          MEASUREMENT_REPORT_KEY
        );
      } else {
        openNotification(
          `${translation.reports.Failed}`,
          'error',
          2,
          MEASUREMENT_REPORT_KEY
        );
      }
    } catch (error) {
      openNotification(
        `${translation.reports.Failed}`,
        'error',
        2,
        MEASUREMENT_REPORT_KEY
      );
    }
  };

  const handlePdfExport = async () => {
    try {
      openNotification(
        `${translation.reports.PdfWait}`,
        'info',
        0,
        MEASUREMENT_REPORT_KEY,
        true
      );

      const excelResponse = await getReportMeasurementsPdf(
        confirmedFromDate,
        confirmedToDate,
        confirmedTaxes,
        confirmedPartner
      );

      const reportId = excelResponse?.data?.[0]?.id;

      if (reportId) {
        await delay(4000);

        const downloadResponse = await waitForReportCompletion(
          String(reportId),
          translation
        );
        const blob = new Blob([downloadResponse.data], {
          type: 'application/pdf',
        });
        saveAs(blob, `report_${reportId}.pdf`);
        openNotification(
          `${translation.reports.PdfGenerated}`,
          'success',
          2,
          MEASUREMENT_REPORT_KEY
        );
      } else {
        openNotification(
          `${translation.reports.Failed}`,
          'error',
          2,
          MEASUREMENT_REPORT_KEY
        );
      }
    } catch (error) {
      openNotification(
        `${translation.reports.Failed}`,
        'error',
        2,
        MEASUREMENT_REPORT_KEY
      );
    }
  };

  // Handle incomes scroll
  const handleIncomesScroll = useCallback(() => {
    if (
      !incomesTableContainerRef.current ||
      !incomesLoadingRef.current ||
      isFetchingIncomesRef.current ||
      !hasMoreIncomes
    )
      return;

    const containerRect =
      incomesTableContainerRef.current.getBoundingClientRect();
    const loadingRect = incomesLoadingRef.current.getBoundingClientRect();

    // Check if the loading element is visible
    if (loadingRect.top <= containerRect.bottom + 100) {
      const nextPage = incomesPageIndex + 1;
      setIncomesPageIndex(nextPage);
      fetchIncomesData(nextPage);
    }
  }, [incomesPageIndex, hasMoreIncomes, fetchIncomesData]);

  // Handle expenses scroll
  const handleExpensesScroll = useCallback(() => {
    if (
      !expensesTableContainerRef.current ||
      !expensesLoadingRef.current ||
      isFetchingExpensesRef.current ||
      !hasMoreExpenses
    )
      return;

    const containerRect =
      expensesTableContainerRef.current.getBoundingClientRect();
    const loadingRect = expensesLoadingRef.current.getBoundingClientRect();

    // Check if the loading element is visible
    if (loadingRect.top <= containerRect.bottom + 100) {
      const nextPage = expensesPageIndex + 1;
      setExpensesPageIndex(nextPage);
      fetchExpensesData(nextPage);
    }
  }, [expensesPageIndex, hasMoreExpenses, fetchExpensesData]);

  // Set up scroll event listeners
  useEffect(() => {
    const incomesContainer = incomesTableContainerRef.current;
    if (incomesContainer) {
      incomesContainer.addEventListener('scroll', handleIncomesScroll);
      return () => {
        incomesContainer.removeEventListener('scroll', handleIncomesScroll);
      };
    }
  }, [handleIncomesScroll]);

  useEffect(() => {
    const expensesContainer = expensesTableContainerRef.current;
    if (expensesContainer && activeTab === 'expenses') {
      expensesContainer.addEventListener('scroll', handleExpensesScroll);
      handleExpensesScroll();
      return () => {
        expensesContainer.removeEventListener('scroll', handleExpensesScroll);
      };
    }
  }, [handleExpensesScroll, activeTab]);

  const isIncomesSelected = activeTab === 'incomes';
  const isShowButtons =
    incomesTableData.length > 0 || expensesTableData.length > 0;

  return (
    <div className="grid h-full grid-cols-1 lg:grid-cols-[repeat(auto-fit,minmax(200px,1fr))]">
      <div className="col-span-4 mb-[20px] md:mb-[0px]">
        {(confirmedFromDate ||
          confirmedToDate ||
          confirmedTaxes ||
          confirmedPartner) && (
          <div className="my-[12px] px-[16px] md:px-[32px]">
            <div className="flex flex-wrap items-center gap-[8px] rounded-[8px] bg-[#F6F6F6] px-[12px] py-[8px] text-[12px] text-[#4B4B4B] md:text-[13px]">
              <span className="font-semibold text-[#1D1D1D]">
                {translation.filter}:
              </span>
              {confirmedFromDate && confirmedToDate && (
                <span className="rounded-[999px] bg-white px-[10px] py-[4px]">
                  {confirmedFromDate} - {confirmedToDate}
                </span>
              )}
              <span className="rounded-[999px] bg-white px-[10px] py-[4px]">
                <span className="font-semibold">
                  {translation.report.Measurement} :{' '}
                </span>
                {isEmpty(taxes)
                  ? translation.reports.All
                  : taxesData
                      ?.filter((option) => taxes?.includes(option.value))
                      .map((option) => option.label)
                      .join(', ')}
              </span>
              <span className="rounded-[999px] bg-white px-[10px] py-[4px]">
                <span className="font-semibold">
                  {translation.reports.Partner} :{' '}
                </span>
                {isEmpty(partner)
                  ? translation.reports.All
                  : partnersData
                      ?.filter((option) => partner?.includes(option.value))
                      .map((option) => option.label)
                      .join(', ')}
              </span>
            </div>
          </div>
        )}
        {isIncomesSelected ? (
          <IncomesTable
            incomesTableContainerRef={incomesTableContainerRef}
            incomesTableData={incomesTableData}
            incomesLoadingRef={incomesLoadingRef}
            isLoadingMoreIncomes={isLoadingMoreIncomes}
            hasMoreIncomes={hasMoreIncomes}
            activeTab={activeTab}
            setActiveTab={setActiveTab}
            setOpen={setOpen}
          />
        ) : (
          <ExpensesTable
            expensesTableContainerRef={expensesTableContainerRef}
            expensesTableData={expensesTableData}
            expensesLoadingRef={expensesLoadingRef}
            isLoadingMoreExpenses={isLoadingMoreExpenses}
            hasMoreExpenses={hasMoreExpenses}
            activeTab={activeTab}
            setActiveTab={setActiveTab}
            setOpen={setOpen}
          />
        )}
      </div>
      {isShowButtons && (
        <div className="h-full  md:border-l md:border-l-[#f5f7f9]">
          <div className="grid grid-cols-[repeat(auto-fit,minmax(200px,1fr))] gap-[12px] px-[16px] md:pt-[12px]">
            <button
              onClick={handleExcelExport}
              className="flex w-full items-center gap-[8px] rounded-[6px] rounded-[6px] border-2  border-[#CECECE] px-[16px] py-[8px] text-[16px] font-normal leading-[16px] text-black transition-all duration-300 ease-in-out hover:bg-gray-100 md:px-[24px] md:py-[12px]"
            >
              <img
                className="mr-[5px] h-5 w-5"
                src="./assets/save-folder-icon.png"
                alt=""
              />
              <p>{translation.reports.SaveAsExcel}</p>
            </button>
            <button
              onClick={handlePdfExport}
              className="flex w-full items-center gap-[8px] rounded-[6px] rounded-[6px] border-2 border-[#CECECE] px-[16px] py-[8px] text-[16px] font-normal leading-[16px] text-black transition-all duration-300 ease-in-out hover:bg-gray-100 md:px-[24px] md:py-[12px]"
            >
              <img
                className="mr-[5px] h-5 w-5"
                src="./assets/save-folder-icon.png"
                alt=""
              />
              <p>{translation.reports.SaveAsPdf}</p>
            </button>
          </div>
          {isIncomesSelected
            ? incomesTableData.length > 0 && (
                <ReportsTotalViewIncome
                  title={translation.reports.SummaryIncome}
                  summaryData={summaryData}
                />
              )
            : expensesTableData.length > 0 && (
                <ReportsTotalViewExpense
                  title={translation.reports.SummaryExpence}
                  summaryData={summaryData}
                />
              )}
        </div>
      )}

      <CustomDialog
        open={open}
        handleClose={() => {
          setOpen(false);
        }}
        title={translation.filter}
        buttonTitle={translation.reports.BtnConfirm}
        onSubmit={handleConfirm}
      >
        <ReportsTab setDateRange={setDateRange} />
        <ReportsMultiSelect
          title={translation.reports.ChooseMeasurement}
          onChange={handleSelect}
          options={taxesData || []}
        />
        <ReportsMultiSelect
          title={translation.reports.ChooseCustomer}
          onChange={handleSelectPartners}
          options={partnersData || []}
        />
      </CustomDialog>
    </div>
  );
};

export default Measurement;
